import { isStructuredEnvelope, readStructuredEnvelope } from '@adornis/web-editor/migrations/structured.js';
import { createDefaultLandingEditorProject, normalizeLandingEditorProject, type LandingEditorProject } from '../../shared/editor/landingEditorTypes.js';
import {
  createLandingEditorWebsiteDocument,
  isLandingEditorWebsiteDocument,
  landingEditorDocumentRevision,
} from '../../shared/editor/landingEditorWebsiteDocument.js';
import { vivaWebsiteRegistry } from './vivaWebsiteRegistry.js';
import type {
  LandingEditorComment,
  LandingEditorCommentCreateInput,
  LandingEditorCommentEvent,
  LandingEditorCommentUpdateInput,
} from '../../shared/editor/landingEditorComments.js';
import type {
  LandingEditorMediaAsset,
  LandingEditorMediaAssetUpdateInput,
} from '../../shared/editor/landingEditorMediaLibrary.js';

type PersistenceTarget = 'backend' | 'local';
export type LandingEditorVersionKind = 'draft' | 'published';

export interface LandingEditorVersionEntry {
  id: string;
  createdAt: string;
  kind: LandingEditorVersionKind;
  project: LandingEditorProject;
}

interface LoginResponse {
  user: { email: string };
}

interface SaveResponse {
  revision: string;
  updatedAt: string;
}

export class LandingEditorRequestError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'LandingEditorRequestError';
  }
}

const userStorageKey = 'vp-landing-editor-user';
const localDraftKey = 'vp-landing-editor-draft';
const localPublishedKey = 'vp-landing-editor-published';
const localHistoryKey = 'vp-landing-editor-history';
const maxVersionHistoryEntries = 30;

class LandingEditorClientService {
  private draftRevision: string | undefined;

  get userEmail() {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem(userStorageKey);
  }

  isAuthenticated() {
    return Boolean(this.userEmail);
  }

  isAuthorizationError(error: unknown) {
    return error instanceof LandingEditorRequestError && (error.status === 401 || error.status === 403);
  }

  async login(email: string, password: string): Promise<PersistenceTarget> {
    const response = await this.request<LoginResponse>('/api/landing-editor/session', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    this.persistSession(response.user.email);
    return 'backend';
  }

  logout() {
    if (typeof window === 'undefined') return;
    window.localStorage.removeItem(userStorageKey);
    void fetch('/api/landing-editor/session', { method: 'DELETE', credentials: 'same-origin' }).catch(() => undefined);
  }

  async loadPublished(): Promise<LandingEditorProject> {
    const fallback = createDefaultLandingEditorProject();
    try {
      const project = await this.request<unknown>('/api/landing-editor/project/published');
      return normalizeLandingEditorProject(project);
    } catch (error) {
      if (this.isNetworkFallbackError(error)) {
        return this.loadLocal(localPublishedKey) ?? fallback;
      }
      return fallback;
    }
  }

  async loadDraft(): Promise<LandingEditorProject> {
    const fallback = createDefaultLandingEditorProject();
    try {
      const project = await this.request<unknown>('/api/landing-editor/project/draft', { auth: true });
      if (isStructuredEnvelope(project)) {
        readStructuredEnvelope(project);
      } else if (isLandingEditorWebsiteDocument(project) && project.format === 'adornis.website') {
        const issues = vivaWebsiteRegistry.validateDocument(project);
        if (issues.length) throw new Error(`Das ProseMirror-Dokument ist ungültig: ${issues[0]?.message ?? 'Unbekannter Fehler'}`);
      }
      this.draftRevision = landingEditorDocumentRevision(project);
      return normalizeLandingEditorProject(project);
    } catch (error) {
      this.logoutOnAuthorizationError(error);
      if (!this.isNetworkFallbackError(error)) throw error;
      return this.loadLocal(localDraftKey) ?? this.loadLocal(localPublishedKey) ?? fallback;
    }
  }

  async saveDraft(project: LandingEditorProject): Promise<PersistenceTarget> {
    const nextProject = { ...project, updatedAt: new Date().toISOString() };
    this.saveLocal(localDraftKey, nextProject);
    this.rememberLocalVersion('draft', nextProject);
    try {
      const result = await this.request<SaveResponse>('/api/landing-editor/project/draft', {
        method: 'PUT',
        auth: true,
        body: JSON.stringify(createLandingEditorWebsiteDocument(nextProject, this.draftRevision)),
      });
      this.draftRevision = result.revision;
      return 'backend';
    } catch (error) {
      this.logoutOnAuthorizationError(error);
      if (!this.isNetworkFallbackError(error)) throw error;
      return 'local';
    }
  }

  async syncDraftFromPublished(): Promise<LandingEditorProject> {
    const project = await this.request<unknown>('/api/landing-editor/project/draft/sync-from-published', {
      method: 'POST',
      auth: true,
    });
    this.draftRevision = landingEditorDocumentRevision(project);
    const syncedProject = normalizeLandingEditorProject(project);
    this.saveLocal(localDraftKey, syncedProject);
    this.saveLocal(localPublishedKey, syncedProject);
    this.rememberLocalVersion('draft', syncedProject);
    return syncedProject;
  }

  async publish(project: LandingEditorProject): Promise<PersistenceTarget> {
    const nextProject = {
      ...project,
      updatedAt: new Date().toISOString(),
      publishedAt: new Date().toISOString(),
    };
    this.saveLocal(localDraftKey, nextProject);
    this.saveLocal(localPublishedKey, nextProject);
    this.rememberLocalVersion('published', nextProject);
    try {
      const result = await this.request<SaveResponse>('/api/landing-editor/project/published', {
        method: 'PUT',
        auth: true,
        body: JSON.stringify(createLandingEditorWebsiteDocument(nextProject, this.draftRevision)),
      });
      this.draftRevision = result.revision;
      return 'backend';
    } catch (error) {
      this.logoutOnAuthorizationError(error);
      if (!this.isNetworkFallbackError(error)) throw error;
      return 'local';
    }
  }

  async loadVersionHistory(): Promise<LandingEditorVersionEntry[]> {
    try {
      const entries = await this.request<unknown>('/api/landing-editor/project/history', { auth: true });
      return this.normalizeHistory(entries);
    } catch (error) {
      this.logoutOnAuthorizationError(error);
      if (!this.isNetworkFallbackError(error)) throw error;
      return this.loadLocalHistory();
    }
  }

  loadComments() {
    return this.request<LandingEditorComment[]>('/api/landing-editor/comments', { auth: true });
  }

  createComment(input: LandingEditorCommentCreateInput) {
    return this.request<LandingEditorComment>('/api/landing-editor/comments', {
      method: 'POST',
      auth: true,
      body: JSON.stringify(input),
    });
  }

  updateComment(id: string, input: LandingEditorCommentUpdateInput) {
    return this.request<LandingEditorComment>(`/api/landing-editor/comments/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      auth: true,
      body: JSON.stringify(input),
    });
  }

  deleteComment(id: string, expectedRevision: number) {
    return this.request<void>(`/api/landing-editor/comments/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      auth: true,
      body: JSON.stringify({ expectedRevision }),
    });
  }

  subscribeComments(onEvent: (event: LandingEditorCommentEvent) => void, onReconnect: () => void) {
    if (typeof window === 'undefined' || typeof EventSource === 'undefined') return () => undefined;
    const source = new EventSource('/api/landing-editor/comments/events', { withCredentials: true });
    source.addEventListener('comment', event => {
      try {
        onEvent(JSON.parse((event as MessageEvent<string>).data) as LandingEditorCommentEvent);
      } catch {
        onReconnect();
      }
    });
    source.addEventListener('open', onReconnect);
    return () => source.close();
  }

  async uploadAsset(file: File): Promise<{ url: string; target: PersistenceTarget; asset?: LandingEditorMediaAsset }> {
    const dataUrl = await this.readFileAsDataUrl(file);
    const base64Data = dataUrl.split(',')[1] ?? '';
    const mimeType = file.type || (file.name.toLowerCase().endsWith('.vtt') ? 'text/vtt' : 'application/octet-stream');
    const normalizedDataUrl = `data:${mimeType};base64,${base64Data}`;

    try {
      const response = await this.request<LandingEditorMediaAsset>('/api/landing-editor/assets', {
        method: 'POST',
        auth: true,
        body: JSON.stringify({ filename: file.name, mimeType, data: base64Data }),
      });
      return { url: response.url, target: 'backend', asset: response };
    } catch (error) {
      this.logoutOnAuthorizationError(error);
      if (!this.isNetworkFallbackError(error)) throw error;
      return { url: normalizedDataUrl, target: 'local' };
    }
  }

  listMediaAssets() {
    return this.request<LandingEditorMediaAsset[]>('/api/landing-editor/assets', { auth: true });
  }

  updateMediaAsset(id: string, input: LandingEditorMediaAssetUpdateInput) {
    return this.request<LandingEditorMediaAsset>(`/api/landing-editor/assets/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      auth: true,
      body: JSON.stringify(input),
    });
  }

  deleteMediaAsset(id: string, expectedRevision: number) {
    return this.request<void>(`/api/landing-editor/assets/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      auth: true,
      body: JSON.stringify({ expectedRevision }),
    });
  }

  private persistSession(email: string) {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(userStorageKey, email);
  }

  private loadLocal(key: string) {
    if (typeof window === 'undefined') return null;
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    try {
      return normalizeLandingEditorProject(JSON.parse(raw));
    } catch {
      return null;
    }
  }

  private loadLocalHistory() {
    if (typeof window === 'undefined') return [];
    const raw = window.localStorage.getItem(localHistoryKey);
    if (!raw) return [];
    try {
      return this.normalizeHistory(JSON.parse(raw));
    } catch {
      return [];
    }
  }

  private saveLocal(key: string, project: LandingEditorProject) {
    if (typeof window === 'undefined') return;
    const payload = JSON.stringify(createLandingEditorWebsiteDocument(project, this.draftRevision));
    try {
      window.localStorage.setItem(key, payload);
    } catch (error) {
      // LocalStorage is only a convenience fallback. Large editor projects or old local version
      // snapshots can exceed the browser quota; publishing to the backend must still continue.
      try {
        window.localStorage.removeItem(localHistoryKey);
        window.localStorage.setItem(key, payload);
      } catch (retryError) {
        console.warn('[landing-editor] Local project cache could not be written:', retryError || error);
      }
    }
  }

  private rememberLocalVersion(kind: LandingEditorVersionKind, project: LandingEditorProject) {
    if (typeof window === 'undefined') return;
    try {
      const entry: LandingEditorVersionEntry = {
        id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        createdAt: new Date().toISOString(),
        kind,
        project: normalizeLandingEditorProject(project),
      };
      const entries = [entry, ...this.loadLocalHistory()].slice(0, maxVersionHistoryEntries);
      window.localStorage.setItem(localHistoryKey, JSON.stringify(entries));
    } catch (error) {
      try {
        window.localStorage.removeItem(localHistoryKey);
      } catch {}
      console.warn('[landing-editor] Local version history could not be written:', error);
    }
  }

  private normalizeHistory(value: unknown): LandingEditorVersionEntry[] {
    if (!Array.isArray(value)) return [];
    return value
      .map(entry => {
        if (!entry || typeof entry !== 'object') return null;
        const raw = entry as Partial<LandingEditorVersionEntry>;
        if (typeof raw.id !== 'string' || typeof raw.createdAt !== 'string') return null;
        if (raw.kind !== 'draft' && raw.kind !== 'published') return null;
        return {
          id: raw.id,
          createdAt: raw.createdAt,
          kind: raw.kind,
          project: normalizeLandingEditorProject(raw.project),
        };
      })
      .filter((entry): entry is LandingEditorVersionEntry => Boolean(entry));
  }

  private logoutOnAuthorizationError(error: unknown) {
    if (this.isAuthorizationError(error)) this.logout();
  }

  private readFileAsDataUrl(file: File) {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  private isNetworkFallbackError(error: unknown) {
    return (
      error instanceof TypeError ||
      error instanceof SyntaxError ||
      (error instanceof Error &&
        (error.message.includes('Failed to fetch') || error.message.includes('fetch failed')))
    );
  }

  private async request<T = unknown>(
    path: string,
    options: RequestInit & { auth?: boolean } = {},
  ): Promise<T> {
    const { auth, ...requestOptions } = options;
    const headers = new Headers(options.headers);
    headers.set('content-type', 'application/json');

    const response = await fetch(path, { ...requestOptions, headers, credentials: 'same-origin' });
    if (!response.ok) {
      const message = await response.text();
      throw new LandingEditorRequestError(response.status, message || `Request failed with ${response.status}`);
    }

    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }
}

export const LandingEditorClient = new LandingEditorClientService();
